import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BottomNavigation from '../components/BottomNavigation';
import { Camera as CameraIcon, UploadSimple, ArrowCounterClockwise, PaperPlaneTilt, CircleNotch } from '@phosphor-icons/react';
import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import ScanningAnimationOverlay from '../components/ScanningAnimationOverlay';
import { useAuth } from '../contexts/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import { testCameraAccess } from '../utils/browserDetection';
import { workerFetch } from '../utils/apiUtils';

// Image compression utility
const compressImage = (base64, quality = 0.7, maxWidth = 800) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedBase64);
    };
    
    img.src = base64;
  });
};

const MODEL_PATH = '/models/face_landmarker.task';
const MIN_FACE_BOX_SIZE = 0.3;
const MAX_FACE_BOX_SIZE = 0.6;
const OPTIMAL_MIN_FACE_BOX_SIZE = 0.40;
const OPTIMAL_MAX_FACE_BOX_SIZE = 0.55;
const MIN_BRIGHTNESS = 50;
const MAX_BRIGHTNESS_SCORE_THRESHOLD = 180;
const TARGET_FACE_OVAL_RATIO = 1.1;
const OVAL_SCALING_FACTOR = 0.6;
const FACE_CONTOUR_INDICES = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152,
  148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109
];
const OVAL_COLOR_DEFAULT = 'rgba(255, 255, 255, 0.5)';
const OVAL_COLOR_GOOD = 'rgba(0, 255, 0, 0.7)';
const OVAL_COLOR_BAD = 'rgba(255, 0, 0, 0.7)';
const MAX_ANGLE_THRESHOLD = 15;

const QualityGauge = ({ score, className }) => {
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const validScore = typeof score === 'number' ? score : 0;
  const offset = circumference - (validScore / 100) * circumference;

  let strokeColor = '#ef4444';
  if (validScore > 70) strokeColor = '#22c55e';
  else if (validScore > 40) strokeColor = '#f59e0b';

  return (
      <div className={`relative w-24 h-24 ${className || ''}`}>
          <svg className="w-full h-full" viewBox="0 0 120 120">
              <circle className="text-gray-600" strokeWidth="10" stroke="currentColor" fill="transparent" r={radius} cx="60" cy="60" />
              <circle
                  className="transition-stroke-dashoffset duration-300 ease-linear"
                  strokeWidth="10"
                  strokeDasharray={circumference}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                  stroke={strokeColor}
                  fill="transparent"
                  r={radius}
                  cx="60"
                  cy="60"
                  transform="rotate(-90 60 60)"
              />
              <text className="text-lg font-bold fill-current text-gray-800" x="50%" y="50%" dominantBaseline="middle" textAnchor="middle">
                  {`${Math.round(validScore)}%`}
              </text>
          </svg>
      </div>
  );
};

const ScanPage = () => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const faceLandmarkerRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const streamRef = useRef(null);

  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { userId, userProfile, token } = useAuth();
  const [kycMessage, setKycMessage] = useState('در حال بارگذاری مدل و دسترسی به دوربین...');
  const [kycDetailedMessage, setKycDetailedMessage] = useState('');
  const [isReadyToCapture, setIsReadyToCapture] = useState(false);
  const [kycLoading, setKycLoading] = useState(true);
  const [qualityScore, setQualityScore] = useState(0);
  const [ovalStatus, setOvalStatus] = useState('default');
  const [individualScores, setIndividualScores] = useState({ presence: 0, distance: 0, angle: 0, brightness: 0 });
  const [videoResolution, setVideoResolution] = useState('');
  const [captureMode, setCaptureMode] = useState(null);

  const navigate = useNavigate();

  const drawOverlay = useCallback((status) => {
    const canvas = overlayCanvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || !video.videoWidth || captureMode !== 'camera') return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const ovalWidth = canvas.width * OVAL_SCALING_FACTOR;
    const ovalHeight = ovalWidth * TARGET_FACE_OVAL_RATIO;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    let strokeColor = OVAL_COLOR_DEFAULT;
    if (status === 'good') strokeColor = OVAL_COLOR_GOOD;
    else if (status === 'bad') strokeColor = OVAL_COLOR_BAD;

    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.ellipse(centerX, centerY, ovalWidth / 2, ovalHeight / 2, 0, 0, 2 * Math.PI);
    ctx.stroke();
  }, [captureMode]);

  useEffect(() => {
    drawOverlay(ovalStatus);
  }, [ovalStatus, drawOverlay]);

  // Browser compatibility is now handled in App.jsx

  useEffect(() => {
    if (captureMode !== 'camera') return;

    console.log("Starting MediaPipe Setup for Camera Mode...");
    setKycLoading(true);
    setIsReadyToCapture(false);
    setQualityScore(0);
    setKycMessage('در حال بارگذاری مدل و دسترسی به دوربین...');
    
    const videoElement = videoRef.current;
    const localMaxAngleThreshold = MAX_ANGLE_THRESHOLD;

    const setupMediaPipe = async () => {
        try {
            setKycMessage('در حال بارگذاری مدل...');
            const vision = await FilesetResolver.forVisionTasks("https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm");
            faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(vision, {
                baseOptions: { modelAssetPath: MODEL_PATH, delegate: "GPU" },
                outputFacialTransformationMatrixes: true,
                runningMode: "VIDEO",
                numFaces: 1
            });

            setKycMessage('درخواست دسترسی به دوربین...');
            
            // Test camera access first
            const cameraAccessible = await testCameraAccess();
            if (!cameraAccessible) {
                setKycMessage('دسترسی به دوربین امکان‌پذیر نیست. لطفاً در مرورگر اصلی باز کنید.');
                setKycLoading(false);
                return;
            }
            
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            streamRef.current = await navigator.mediaDevices.getUserMedia({
                video: { width: 640, height: 480, facingMode: 'user' },
                audio: false
            });

            if (videoElement) {
                videoElement.srcObject = streamRef.current;
                videoElement.onloadedmetadata = () => {
                    console.log("Video metadata loaded. Starting prediction loop.");
                    setVideoResolution(`${videoElement.videoWidth}x${videoElement.videoHeight}`);
                    setKycLoading(false);
                    drawOverlay(ovalStatus);
                    requestAnimationFrame(predictWebcam);
                };
            } else {
                 throw new Error("Video element not found");
            }
        } catch (error) {
            console.error("Failed to initialize MediaPipe or webcam:", error);
            setKycMessage(`خطا: ${error.message}. لطفا دسترسی دوربین را بررسی کنید.`);
            setError(`خطا در دوربین: ${error.message}`);
            setKycLoading(false);
            setCaptureMode(null);
        }
    };

    const predictWebcam = () => {
        if (!faceLandmarkerRef.current || !videoElement || videoElement.readyState < 4 || captureMode !== 'camera') {
            if (captureMode !== 'camera') console.log("Stopping predict loop: Not in camera mode.");
            return;
        }

        const startTimeMs = performance.now();
        const results = faceLandmarkerRef.current.detectForVideo(videoElement, startTimeMs);

        let totalScore = 0;
        let primaryMsg = "";
        let secondaryMsg = "";
        let isReady = false;
        let currentOvalStatus = 'bad';
        const currentScores = { presence: 0, distance: 0, angle: 0, brightness: 0 };
        const currentAngleDetails = { roll: 99, pitch: 99, yaw: 99 };

        if (!results.faceLandmarks || results.faceLandmarks.length === 0 || !results.facialTransformationMatrixes || results.facialTransformationMatrixes.length === 0) {
            primaryMsg = "چهره‌ای شناسایی نشد. لطفا صورت خود را کامل در کادر بیضی قرار دهید.";
            setQualityScore(0);
            setKycMessage(primaryMsg);
            setKycDetailedMessage("");
            setIsReadyToCapture(false);
            setOvalStatus('bad');
            setIndividualScores(currentScores);
            animationFrameIdRef.current = requestAnimationFrame(predictWebcam);
            return;
        }

        currentScores.presence = 100;
        primaryMsg = "چهره شناسایی شد.";
        const landmarks = results.faceLandmarks[0];
        const matrix = results.facialTransformationMatrixes[0].data;
        const videoWidth = videoElement.videoWidth;
        const videoHeight = videoElement.videoHeight;

        let positionOK = false;
        currentScores.distance = 0;
        currentOvalStatus = 'bad';

        const ovalCenterX = videoWidth / 2;
        const ovalCenterY = videoHeight / 2;
        const ovalRadiusX = (videoWidth * OVAL_SCALING_FACTOR) / 2;
        const ovalRadiusY = (videoWidth * OVAL_SCALING_FACTOR * TARGET_FACE_OVAL_RATIO) / 2;
        const radiusXSquared = ovalRadiusX * ovalRadiusX;
        const radiusYSquared = ovalRadiusY * ovalRadiusY;

        let allPointsInside = true;
        if (radiusXSquared > 0 && radiusYSquared > 0) {
             for (const index of FACE_CONTOUR_INDICES) {
                 if (index >= landmarks.length) continue;
                 const point = landmarks[index];
                 const px = point.x * videoWidth;
                 const py = point.y * videoHeight;
                 const dx = px - ovalCenterX;
                 const dy = py - ovalCenterY;
                 if (((dx * dx) / radiusXSquared) + ((dy * dy) / radiusYSquared) > 1.05) {
                     allPointsInside = false;
                     break;
                 }
             }
        } else {
            allPointsInside = false;
        }

         if (allPointsInside) {
             const { xMin, xMax, yMin, yMax } = getBoundingBox(landmarks, videoWidth, videoHeight);
             const faceWidth = xMax - xMin;
             const faceHeight = yMax - yMin;
             const relativeWidth = faceWidth / videoWidth;
             const relativeHeight = faceHeight / videoHeight;
             const avgSize = (relativeWidth + relativeHeight) / 2;
             
             if (avgSize >= OPTIMAL_MIN_FACE_BOX_SIZE) {
                 positionOK = true;
                 currentOvalStatus = 'good';
                 primaryMsg = "موقعیت چهره مناسب است.";
                 currentScores.distance = 100;
             } else {
                 positionOK = false;
                 currentOvalStatus = 'bad';
                 primaryMsg = "لطفا کمی نزدیک‌تر شوید.";
                 currentScores.distance = Math.max(0, Math.round((avgSize / OPTIMAL_MIN_FACE_BOX_SIZE) * 69));
             }
         } else {
             positionOK = false;
             currentOvalStatus = 'bad';
             primaryMsg = "لطفا چهره خود را کامل در کادر بیضی قرار دهید.";
             currentScores.distance = 0;
         }

          if (!positionOK) {
              totalScore = Math.round((currentScores.distance + currentScores.angle + currentScores.brightness) / 3);
              setQualityScore(totalScore);
              setKycMessage(primaryMsg);
              setKycDetailedMessage("");
              setIsReadyToCapture(false);
              setOvalStatus(currentOvalStatus);
              setIndividualScores(currentScores);
              animationFrameIdRef.current = requestAnimationFrame(predictWebcam);
              return;
          }

        const { roll, pitch, yaw } = getEulerAnglesFromMatrix(matrix);
        currentAngleDetails.roll = roll; currentAngleDetails.pitch = pitch; currentAngleDetails.yaw = yaw;
        const angleRollOK = Math.abs(roll) <= localMaxAngleThreshold;
        const anglePitchOK = Math.abs(pitch) <= localMaxAngleThreshold;
        const angleYawOK = Math.abs(yaw) <= localMaxAngleThreshold;
        const allAnglesOK = angleRollOK && anglePitchOK && angleYawOK;
        let angleScore = 0;
        if(angleRollOK) angleScore += 34; if(anglePitchOK) angleScore += 33; if(angleYawOK) angleScore += 33;
        currentScores.angle = Math.min(100, Math.round(angleScore));

        if (!allAnglesOK) {
            primaryMsg = "لطفا سر خود را صاف نگه دارید.";
            currentOvalStatus = 'bad';
            if (!angleRollOK) secondaryMsg = `کجی سر: ${roll.toFixed(1)}°`;
            else if (!anglePitchOK) secondaryMsg = `زاویه بالا/پایین: ${pitch.toFixed(1)}°`;
            else secondaryMsg = `چرخش سر: ${yaw.toFixed(1)}°`;
             totalScore = Math.round((currentScores.distance + currentScores.angle + currentScores.brightness) / 3);
             setQualityScore(totalScore);
             setKycMessage(primaryMsg);
             setKycDetailedMessage(secondaryMsg);
             setIsReadyToCapture(false);
             setOvalStatus(currentOvalStatus);
             setIndividualScores(currentScores);
             animationFrameIdRef.current = requestAnimationFrame(predictWebcam);
             return;
        } else {
            primaryMsg = "زاویه سر مناسب است.";
            secondaryMsg = "";
        }

        const brightnessValue = analyzeBrightness(videoElement);
        let brightOK = false;
        let brightnessScoreCalc = 0;
        if (brightnessValue < MIN_BRIGHTNESS) {
            brightnessScoreCalc = Math.max(0, Math.round((brightnessValue / MIN_BRIGHTNESS) * 70));
            primaryMsg = "محیط تاریک است. لطفا نور را بیشتر کنید.";
            currentOvalStatus = 'bad';
            secondaryMsg = "";
        } else {
            brightOK = true;
            if (brightnessValue > MAX_BRIGHTNESS_SCORE_THRESHOLD) {
                brightnessScoreCalc = Math.max(70, Math.round(100 - (brightnessValue - MAX_BRIGHTNESS_SCORE_THRESHOLD)/5 ));
            } else {
                brightnessScoreCalc = 100;
            }
            primaryMsg = "نور محیط مناسب است.";
            secondaryMsg = "";
        }
        currentScores.brightness = Math.max(0, Math.min(100, brightnessScoreCalc));

        if (!brightOK) {
             totalScore = Math.round((currentScores.distance + currentScores.angle + currentScores.brightness) / 3);
             setQualityScore(totalScore);
             setKycMessage(primaryMsg);
             setKycDetailedMessage(secondaryMsg);
             setIsReadyToCapture(false);
             setOvalStatus(currentOvalStatus);
             setIndividualScores(currentScores);
             animationFrameIdRef.current = requestAnimationFrame(predictWebcam);
             return;
        }

        totalScore = 100;
        isReady = true;
        primaryMsg = "عالی! آماده ثبت تصویر.";
        secondaryMsg = "لطفا عینک خود را بردارید (اگر استفاده می‌کنید).";
        currentOvalStatus = 'good';
        currentScores.distance = 100; currentScores.angle = 100; currentScores.brightness = 100;

        setQualityScore(totalScore);
        setKycMessage(primaryMsg);
        setKycDetailedMessage(secondaryMsg);
        setIsReadyToCapture(isReady);
        setOvalStatus(currentOvalStatus);
        setIndividualScores(currentScores);

        animationFrameIdRef.current = requestAnimationFrame(predictWebcam);
    };

    setupMediaPipe();

    return () => {
        console.log("Cleanup: Stopping camera and MediaPipe");
        cancelAnimationFrame(animationFrameIdRef.current);
        if (faceLandmarkerRef.current) {
            faceLandmarkerRef.current.close();
            faceLandmarkerRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
         if (videoRef.current && videoRef.current.srcObject) {
              videoRef.current.srcObject = null;
         }
        setKycLoading(false);
        setIsReadyToCapture(false);
        setQualityScore(0);
        setIndividualScores({ presence: 0, distance: 0, angle: 0, brightness: 0 });
        setVideoResolution('');
        setKycMessage('');
        setKycDetailedMessage('');
    };
  }, [captureMode]);

  const handleUploadClick = () => {
    if (captureMode === 'camera') {
        setCaptureMode(null);
    }
    setImage(null);
    fileInputRef.current?.click();
  };

  const handleCameraUploadClick = () => {
    setImage(null);
    cameraInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Determine capture mode based on which input was used
    const isFromCamera = event.target === cameraInputRef.current;
    setCaptureMode(isFromCamera ? 'camera_upload' : 'upload');
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result);
      console.log(isFromCamera ? 'Photo captured from camera.' : 'Photo uploaded from device.');
    };
    reader.onerror = (err) => {
        console.error("Error reading file:", err);
        setError("خطا در بارگذاری فایل عکس.");
    };
    reader.readAsDataURL(file);
  };

  const handleRetake = () => {
    setImage(null);
    setError(null);
    setLoading(false);
    
    // For camera_upload mode, trigger camera input again
    if (captureMode === 'camera_upload') {
      cameraInputRef.current?.click();
    }
    // For camera mode, it will restart the camera stream automatically
  };

  const chooseDifferentPhoto = () => {
    setImage(null);
    setError(null);
    setLoading(false);
    setCaptureMode(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = "";
    }
  };

  const handleCaptureValidatedImage = () => {
    if (!videoRef.current || !isReadyToCapture || captureMode !== 'camera') return;

    console.log("Capturing validated image...");
    setLoading(true);

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageDataUrl = canvas.toDataURL('image/png');

    setCaptureMode(null);

    setImage(imageDataUrl);
    setLoading(false);

    console.log('Validated image captured. Ready for analysis.');
  };

  const analyzeSkin = async () => {
    if (!image) {
        setError("لطفا ابتدا یک عکس معتبر بگیرید یا آپلود کنید.");
        return;
    }
    if (!userId || userProfile === null) {
        setError("اطلاعات کاربر بارگذاری نشده است. لطفا لحظاتی بعد دوباره تلاش کنید.");
        console.error("Analyze Error: userId or userProfile not available from context.", { userId, userProfile });
        return; 
    }

    setError(null);
    setLoading(true);
    console.log('Starting skin analysis for', captureMode === 'upload' ? 'uploaded image' : 'captured image');
    console.log('Using profile from context:', userProfile);

    const ageForPrompt = userProfile?.age ?? 'نامشخص';
    const genderForPrompt = userProfile?.gender ?? 'نامشخص';
    const lifestyleForPrompt = {
        sleep: userProfile?.lifestyle?.sleep ?? 'نامشخص',
        stress: userProfile?.lifestyle?.stress ?? 'نامشخص',
        smoking: userProfile?.lifestyle?.smoking ?? 'نامشخص',
        exercise: userProfile?.lifestyle?.exercise ?? 'نامشخص',
        diet: userProfile?.lifestyle?.diet ?? 'نامشخص',
        water: userProfile?.lifestyle?.water ?? 'نامشخص',
    };

    const imageBase64 = image.startsWith('data:') ? image : `data:image/jpeg;base64,${image}`;
    console.log('Image converted to base64 (first 100 chars): ', imageBase64.substring(0, 100));

    if (!token) {
      setError("Authentication token not found. Cannot analyze skin.");
      setLoading(false);
      return;
    }

    try {
      console.log('Sending analysis request to Worker (Worker will call OpenAI)...');

      // Call Worker endpoint instead of OpenAI directly (avoids CORS and network issues)
      const analysisResponse = await workerFetch('/api/analysis/analyze', {
          method: 'POST',
          headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
          },
          body: JSON.stringify({
              image: imageBase64,
              userProfile: userProfile
          })
      }, 3); // 3 retries with timeout

      console.log('Analysis response received from Worker:', analysisResponse);

      let analysisResult = null;
      if (analysisResponse && analysisResponse.analysis) {
          analysisResult = analysisResponse.analysis;
          console.log('Successfully received analysis from Worker');
      } else {
          throw new Error(analysisResponse?.error || 'Failed to get analysis from Worker');
      }

      // Use the original image directly for analysis
      // Compress image before sending to reduce document size
      console.log('Compressing image for analysis...');
      const compressedImage = await compressImage(image, 0.7, 800); // 70% quality, max 800px width
      const downloadURL = compressedImage;

      if (!token) {
          throw new Error("Authentication token not found from context. Cannot save analysis.");
      }

      console.log("Sending analysis data to worker: /api/analysis");

      const analysisPayload = {
          imageUrl: downloadURL,
          analysis: analysisResult,
          originalImage: image, // Store original image as well
      };

      // Use workerFetch with timeout and retry logic
      const savedAnalysis = await workerFetch('/api/analysis', {
          method: 'POST',
          headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
          },
          body: JSON.stringify(analysisPayload)
      }, 3); // 3 retries
      console.log('Analysis saved successfully via worker:', savedAnalysis);
      
      // Check if response has error
      if (savedAnalysis && savedAnalysis.error) {
          throw new Error(savedAnalysis.error || savedAnalysis.message || 'Worker failed to save analysis');
      }

      if (savedAnalysis && savedAnalysis.id) {
          // Track skin analysis event
          if (window.trackSkinAnalysis) {
              window.trackSkinAnalysis();
          }
          
          navigate(`/analysis/${savedAnalysis.id}`);
      } else {
          console.error("Worker did not return analysis with ID after saving.");
          setError("تحلیل ذخیره شد اما دریافت شناسه آن با مشکل مواجه شد.");
          navigate('/history');
      }

    } catch (err) {
      console.error('Error during analysis or saving:', err);
      
      // Handle different types of errors
      if (err.name === 'TypeError' && err.message.includes('Failed to fetch')) {
        // Network error - connection issue with Worker
        console.error('Network Error: Failed to connect to Worker');
        setError('خطا در اتصال به سرور. لطفاً اتصال اینترنت خود را بررسی کنید و دوباره تلاش کنید.');
      } else if (err.name === 'AbortError') {
        // Timeout error
        console.error('Timeout Error: Request took too long');
        setError('درخواست شما زمان زیادی طول کشید. لطفاً دوباره تلاش کنید.');
      } else if (err.message) {
        // Other errors with message (from Worker)
        console.error('Error message:', err.message);
        if (err.message.includes('OpenAI API Error')) {
          setError('خطا در ارتباط با سرویس هوش مصنوعی. لطفاً چند لحظه صبر کنید و دوباره تلاش کنید.');
        } else {
          setError(err.message);
        }
      } else {
        // Unknown error
        console.error('Unknown error:', err);
        setError('یک خطای ناشناخته در فرآیند آنالیز رخ داد. لطفاً دوباره تلاش کنید.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getBoundingBox = (landmarks, width, height) => {
    let xMin = width, xMax = 0, yMin = height, yMax = 0;
    landmarks.forEach(point => {
        const x = point.x * width;
        const y = point.y * height;
        if (x < xMin) xMin = x;
        if (x > xMax) xMax = x;
        if (y < yMin) yMin = y;
        if (y > yMax) yMax = y;
    });
    return { xMin, xMax, yMin, yMax };
  };

  const getEulerAnglesFromMatrix = (matrixData) => {
    let yaw, pitch, roll;
    const radToDeg = 180 / Math.PI;
    const sinPitch = -matrixData[2];
    if (Math.abs(sinPitch) >= 0.99999) {
        pitch = Math.sign(sinPitch) * (Math.PI / 2);
        roll = 0;
        yaw = Math.atan2(-matrixData[4], matrixData[0]);
    } else {
        pitch = Math.asin(sinPitch);
        yaw = Math.atan2(matrixData[1], matrixData[0]);
        roll = Math.atan2(matrixData[6], matrixData[10]);
    }
    return { roll: roll * radToDeg, pitch: pitch * radToDeg, yaw: yaw * radToDeg };
  };

  const analyzeBrightness = (video) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!video.videoWidth) return 0;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        let sum = 0;
        for (let i = 0; i < data.length; i += 4) {
            sum += (data[i] + data[i + 1] + data[i + 2]) / 3;
        }
        return sum / (data.length / 4);
    } catch (e) {
        console.error("Brightness analysis failed:", e);
        return 0;
    }
  };

  if (!userId || !userProfile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-12 w-12 border-4 border-black border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-100 text-gray-800 pt-6 pb-20" dir="rtl">
      <LoadingOverlay 
        isLoading={loading} 
        message="در حال آنالیز پوست..."
      />
        <div className="flex-grow flex flex-col items-center justify-center p-4 mt-10">
            {!captureMode && !image && (
                <div className="bg-white p-6 rounded-lg shadow-md mx-auto max-w-lg space-y-6 text-gray-800 text-center">
                    <h2 className="text-2xl font-bold text-gray-900">آنالیز هوشمند پوست با هوش مصنوعی</h2>
                    <p className="text-base text-gray-700">
                        این سرویس با استفاده از الگوریتم‌های پیشرفته هوش مصنوعی و بینایی ماشین، ویژگی‌های سطحی پوست شما مانند چربی، خشکی، منافذ، لک‌ها و چروک‌ها را تحلیل می‌کند و توصیه‌های شخصی‌سازی‌شده برای بهبود سلامت پوست ارائه می‌دهد.
                    </p>
                    <p className="text-sm text-gray-600">لطفاً قبل از ادامه، نکات زیر را در نظر بگیرید:</p>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                        <li>نور ملایم و یکنواخت</li>
                        <li>بدون آرایش یا کرم‌های سنگین</li>
                        <li>بدون عینک یا سایر وسایل پوشاننده صورت</li>
                        <li>تصویر واضح و فوکوس‌شده</li>
                        <li>صورت بدون پوشش (موها از صورت کنار زده باشند)</li>
                    </ul>
            <div className="flex flex-col gap-4 justify-center">
              <button
                            onClick={() => setCaptureMode('camera')}
                            className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-full shadow-lg transition duration-300 ease-in-out"
              >
                <CameraIcon size={20} />
                استفاده از دوربین (استریم)
              </button>
              <button
                onClick={handleCameraUploadClick}
                            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-full shadow-lg transition duration-300 ease-in-out"
              >
                <CameraIcon size={20} />
                آپلود عکس از دوربین
              </button>
              <button
                onClick={handleUploadClick}
                            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full shadow-lg transition duration-300 ease-in-out"
              >
                <UploadSimple size={20} />
                بارگذاری عکس از گالری
              </button>
            </div>
            <input 
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
             />
            <input 
                type="file"
                ref={cameraInputRef}
                onChange={handleFileChange}
                accept="image/*"
                capture="environment"
                className="hidden"
             />
          </div>
        )}

            {captureMode === 'camera' && !image && (
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mx-auto w-full max-w-lg text-center space-y-4 relative">
                    <h3 className="text-lg font-semibold text-gray-900">مرحله ۲: ثبت عکس</h3>
                    <p className="text-sm text-gray-600 mb-2">صورت خود را درون قاب قرار دهید و ثابت نگه دارید.</p>
                    
                    <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden bg-black shadow-lg mx-auto">
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-500 ${kycLoading ? 'opacity-0' : 'opacity-100'}`}
                            style={{ transform: 'scaleX(-1)' }}
                            onLoadedMetadata={() => { drawOverlay(ovalStatus); }}
                        ></video>
                        <canvas
                            ref={overlayCanvasRef}
                            className="absolute top-0 left-0 w-full h-full pointer-events-none z-10"
                        />

                        {/* KYC Loading Overlay */}
                        {kycLoading && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-80 text-white z-30">
                                <CircleNotch size={32} className="animate-spin mb-2" />
                                <span>{kycMessage}</span>
                            </div>
                        )}

                        {/* KYC Messages now positioned directly below the video frame */}
                        {!kycLoading && (kycMessage || kycDetailedMessage) && (
                            <div className="bg-gray-800 text-white p-3 rounded-b-lg text-center text-sm shadow-md -mt-2 relative z-0">
                                {/* -mt-2 to visually attach to the bottom of the video frame if the video frame has rounded corners. Adjust as needed. */}
                                {/* z-0 to ensure it's behind any potential absolute elements from below if structure changes */}
                                {kycMessage && <p className="font-semibold mb-0.5">{kycMessage}</p>}
                                {kycDetailedMessage && <p className="text-amber-400 font-medium">{kycDetailedMessage}</p>}
                            </div>
                        )}

                        {/* Capture Button Overlaid on Video */}
                        <div className="absolute bottom-4 left-4 right-4 z-20">
                            <button
                                onClick={handleCaptureValidatedImage}
                                disabled={!isReadyToCapture || loading}
                                className={`w-full px-6 py-3.5 rounded-xl font-semibold text-white transition-all duration-300 flex items-center justify-center space-x-2 space-x-reverse text-base sm:text-lg shadow-lg ${
                                    isReadyToCapture
                                        ? 'bg-green-500 hover:bg-green-600 transform hover:scale-105'
                                        : 'bg-gray-500 cursor-not-allowed opacity-80'
                                }`}
                            >
                                {loading ? <CircleNotch size={24} className="animate-spin" /> : <CameraIcon size={24} weight="fill" />}
                                <span>{loading ? 'صبر کنید...' : isReadyToCapture ? 'ثبت عکس' : (qualityScore > 0 ? `کیفیت: ${qualityScore.toFixed(0)}%` : 'بررسی شرایط...')}</span>
                            </button>
                        </div>
                    </div>

                    {/* Elements BELOW the video frame AND messages, visible when not KYC loading */}
                    {!kycLoading && (
                        <div className="mt-4 space-y-4">
                            <QualityGauge score={qualityScore} className="mx-auto w-20 h-20 sm:w-24 sm:h-24" />
                            
                            <div className="text-xs text-gray-700 grid grid-cols-2 gap-x-3 gap-y-1.5 w-full max-w-xs mx-auto text-center p-2 rounded-lg border border-gray-200 bg-gray-50 shadow-sm">
                                <span><span className="text-gray-500">وضوح:</span> <span className="font-mono text-gray-800 font-medium">{videoResolution || '-'}</span></span>
                                <span><span className="text-gray-500">روشنایی:</span> <span className="font-mono text-gray-800 font-medium">{individualScores.brightness.toFixed(0)}%</span></span>
                                <span><span className="text-gray-500">موقعیت:</span> <span className="font-mono text-gray-800 font-medium">{individualScores.distance.toFixed(0)}%</span></span>
                                <span><span className="text-gray-500">زاویه:</span> <span className="font-mono text-gray-800 font-medium">{individualScores.angle.toFixed(0)}%</span></span>
                            </div>

                            <button 
                                onClick={chooseDifferentPhoto} 
                                disabled={loading}
                                className="text-sm text-center w-full max-w-xs mx-auto py-2.5 px-4 text-gray-700 hover:text-black transition-colors rounded-lg hover:bg-gray-100 border border-gray-300 shadow-sm font-medium"
                            >
                                انصراف و بازگشت
                            </button>
                        </div>
                    )}
                </div>
            )}

        {image && (
                <div className="bg-white p-6 rounded-lg shadow-md mx-auto max-w-lg text-center space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">مرحله ۳: بازبینی و آنالیز</h3>
                    <div className="relative w-full h-auto rounded-lg shadow-lg mb-4">
                        <img src={image} alt="پیش نمایش اسکن پوست" className="w-full h-auto rounded-lg" />
                        {loading && <ScanningAnimationOverlay />}
                    </div>
                    {error && <p className="text-red-500 text-sm mt-4 text-center">خطا: {error}</p>}
                    <div className="space-y-3">
                <button
                            onClick={captureMode === 'camera' || captureMode === 'camera_upload' ? handleRetake : chooseDifferentPhoto}
                            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 rounded-lg flex items-center justify-center space-x-2 space-x-reverse transition-colors"
                >
                            <ArrowCounterClockwise size={20} />
                            <span>{captureMode === 'camera' || captureMode === 'camera_upload' ? 'گرفتن عکس مجدد' : 'انتخاب عکس دیگر'}</span>
                </button>
                <button
                  onClick={analyzeSkin}
                            disabled={loading}
                            className={`w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded-lg flex items-center justify-center space-x-2 space-x-reverse transition-colors ${loading ? 'bg-blue-300 cursor-not-allowed' : ''}`}
                >
                            {loading ? <CircleNotch size={20} className="animate-spin" /> : <PaperPlaneTilt size={20} />}
                            <span>{loading ? 'در حال آنالیز...' : 'آنالیز پوست'}</span>
                </button>
                    </div>
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default ScanPage;